import type { TFunction } from 'i18next';
import {
  isAppointmentPassed,
  isInProgressPastEnd,
  type AppointmentRead,
} from '../../../helpers/api/appointment/appointment';

export type AppointmentStatusKey =
  | 'scheduled'
  | 'inProgress'
  | 'completed'
  | 'noShow'
  | 'cancelled'
  | 'draft'
  | 'passed';

export type StatusVisual = {
  statusKey: AppointmentStatusKey;
  label: string;
  badgeClass: string;
  accentClass: string;
  dotClass: string;
  isLocked: boolean;
};

/** Derive display status from API status + wall clock for passed/finished slots. */
export function getAppointmentStatusVisual(
  app: Pick<
    AppointmentRead,
    'status' | 'start_time' | 'end_time' | 'duration_minutes'
  > | { status?: string; start_time?: string; end_time?: string; duration_minutes?: number },
  t: TFunction<'Scheduling'>
): StatusVisual {
  const s = String(app.status || 'scheduled')
    .toLowerCase()
    .replace(/^.*\./, '');

  const finishedVisual = (): StatusVisual => ({
    statusKey: 'completed',
    label: t('status.finished', { defaultValue: t('status.completed') }),
    badgeClass:
      'bg-slate-100 text-slate-600 border-slate-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600',
    accentClass: 'border-l-slate-400',
    dotClass: 'bg-slate-400',
    isLocked: true,
  });

  if (s === 'completed' || s === 'finished') {
    return finishedVisual();
  }

  if (
    s === 'in_progress' &&
    app.start_time &&
    isInProgressPastEnd(
      app as Pick<
        AppointmentRead,
        'status' | 'start_time' | 'end_time' | 'duration_minutes'
      >
    )
  ) {
    return finishedVisual();
  }

  if (s === 'in_progress') {
    return {
      statusKey: 'inProgress',
      label: t('status.inProgress'),
      badgeClass:
        'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
      accentClass: 'border-l-emerald-500',
      dotClass: 'bg-emerald-500 animate-pulse',
      isLocked: false,
    };
  }
  if (s === 'no_show') {
    return {
      statusKey: 'noShow',
      label: t('status.noShow', { defaultValue: 'No Show' }),
      badgeClass:
        'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
      accentClass: 'border-l-red-500',
      dotClass: 'bg-red-500',
      isLocked: true,
    };
  }
  if (s === 'cancelled') {
    return {
      statusKey: 'cancelled',
      label: t('status.cancelled', { defaultValue: 'Cancelled' }),
      badgeClass:
        'bg-slate-50 text-slate-400 border-slate-100 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700',
      accentClass: 'border-l-slate-300',
      dotClass: 'bg-slate-300',
      isLocked: true,
    };
  }
  if (s === 'draft') {
    return {
      statusKey: 'draft',
      label: t('status.draft', { defaultValue: 'Draft' }),
      badgeClass:
        'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
      accentClass: 'border-l-amber-500',
      dotClass: 'bg-amber-500',
      isLocked: false,
    };
  }

  if (
    (s === 'scheduled' || s === 'confirmed') &&
    app.start_time &&
    isAppointmentPassed(
      app as Pick<
        AppointmentRead,
        'status' | 'start_time' | 'end_time' | 'duration_minutes'
      >
    )
  ) {
    return {
      statusKey: 'passed',
      label: t('status.passed', { defaultValue: 'Passed' }),
      badgeClass:
        'bg-slate-100 text-slate-500 border-slate-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600',
      accentClass: 'border-l-slate-400',
      dotClass: 'bg-slate-400',
      isLocked: false,
    };
  }

  return {
    statusKey: 'scheduled',
    label: t('status.scheduled'),
    badgeClass:
      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    accentClass: 'border-l-blue-500',
    dotClass: 'bg-blue-500',
    isLocked: false,
  };
}

export function getPriorityVisual(
  priority: string | undefined,
  t: TFunction<'Scheduling'>
): { label: string; className: string } | null {
  const p = (priority || 'ROUTINE').toUpperCase();
  if (p === 'STAT') {
    return {
      label: t('priority.stat', { defaultValue: 'STAT' }),
      className: 'bg-red-600 text-white',
    };
  }
  if (p === 'URGENT') {
    return {
      label: t('priority.urgent', { defaultValue: 'URGENT' }),
      className: 'bg-amber-500 text-white',
    };
  }
  return null;
}

export function formatStaffName(
  first?: string | null,
  last?: string | null,
  email?: string | null,
  fallbackById?: Map<string, string>,
  id?: string
) {
  const name = [first, last].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (email) return email;
  if (id && fallbackById) return fallbackById.get(String(id)) ?? '';
  return '';
}
